// ProfileTab.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import { getLangFromPath, generateLangPath } from '../resource/langPath.js';
import {
  ProfileListType,
  ProfileListPublishStatus,
} from '../resource/profileConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  NULL_FUNCTION,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
} from '../resource/defaults.js';

import { color, headerHeight, breakpoint } from '../style/variables.js';
import media from '../style/media.js';

const tabObject = {
  [ProfileListType.HOME]: {
    titleI18nKey: 'home_page',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileHome,
  },
  [ProfileListType.STORY]: {
    titleI18nKey: 'broadcast',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileStory,
  },
  [ProfileListType.SHORT]: {
    titleI18nKey: 'profile_shorts',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileShorts,
  },
  [ProfileListType.FLIX]: {
    titleI18nKey: 'title_flix',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileVideo,
  },
  [ProfileListType.IMAGE]: {
    titleI18nKey: 'profile_photo',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileImage,
  },
  [ProfileListType.LIVESTREAM_SESSION]: {
    titleI18nKey: 'LIVE',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileLive,
  },
};
const groupTabObject = {
  [ProfileListPublishStatus.PUBLISHED]: {
    titleI18nKey: 'profile_published',
    mixpanelButtonId: ButtonId.Tab.TabPublished,
  },
  [ProfileListPublishStatus.DRAFT]: {
    titleI18nKey: 'profile_unpublished',
    mixpanelButtonId: ButtonId.Tab.TabUnpublished,
  },
};

const tabStyle = {
  marginRight: '24px',
  flex: 'none',
};
const lastTabStyle = {
  ...tabStyle,
  marginRight: '0px',
};
const tabLabelStyle = {
  margin: '0px',
  opacity: 1,
};

export const ProfileTab = ({
  userId = null,
  deviceWidth = 0,
  profileTabs: propsProfileTabs = EMPTY_ARRAY,
  profileTabsDraft: propsProfileTabsDraft = EMPTY_ARRAY,
  profileTabsGroup: propsProfileTabsGroup = EMPTY_ARRAY,
  isMeProfile = false,
  listType = null,
  publishStatus = null,
  replace = NULL_FUNCTION,
  location = EMPTY_OBJECT,
}) => {
  const { t } = useTranslation();
  const isOnMobile = deviceWidth <= breakpoint.mobile;
  const isPublished =
    publishStatus && ProfileListPublishStatus.PUBLISHED === publishStatus;
  const listTabs = useMemo(() => {
    const tabs = isPublished ? propsProfileTabs : propsProfileTabsDraft;
    if (isMeProfile) {
      return tabs;
    }
    // Should filter out livestream session tab if is user profile and is published tab.
    return tabs.filter(tabId => ProfileListType.LIVESTREAM_SESSION !== tabId);
  }, [isMeProfile, isPublished, propsProfileTabs, propsProfileTabsDraft]);

  const getPath = useCallback(
    ({ listType: argListType, publishStatus: argPublishStatus }) => {
      const { search, pathname: currentPathname } = location;
      const currentLang = getLangFromPath(currentPathname);
      const searchParams = new URLSearchParams(search);
      ['feed', '_feed', 'filters', '_filters', 'sorts', '_sorts'].forEach(key =>
        searchParams.delete(key)
      );
      const isPublished =
        ProfileListPublishStatus.PUBLISHED === argPublishStatus;
      const tabs = isPublished ? propsProfileTabs : propsProfileTabsDraft;
      const listType = tabs.includes(argListType) ? argListType : tabs[0];
      let basePath = `/user/${userId}/${listType}/${argPublishStatus}`;
      // Use `/user/:userId` if is on published tab and first list tab
      if (isPublished && argListType === propsProfileTabs[0]) {
        basePath = `/user/${userId}`;
        // Use `/user/:userId/:listType` if is on published tab
      } else if (isPublished) {
        basePath = `/user/${userId}/${listType}`;
      }
      return {
        pathname: generateLangPath(basePath, currentLang),
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      };
    },
    [location, propsProfileTabs, propsProfileTabsDraft, userId]
  );

  const handleTabChange = useCallback(
    (event, value) => {
      // to invalid anchor's default behavior (since we don't wanna cause reload)
      event?.preventDefault();
      return replace(getPath({ listType: value, publishStatus }));
    },
    [getPath, publishStatus, replace]
  );
  const handlePublishStatusChange = useCallback(
    (event, value) => {
      return replace(getPath({ listType, publishStatus: value }));
    },
    [getPath, listType, replace]
  );

  const GroupsTabsComponent = useMemo(() => {
    if (!isMeProfile) {
      return null;
    }
    return (
      <GroupTabs>
        <Tabs value={publishStatus} onChange={handlePublishStatusChange}>
          {propsProfileTabsGroup.map((tabId, index) => {
            const isActive = tabId === publishStatus;
            const i18nKey = groupTabObject[tabId]?.titleI18nKey;
            const title = t(i18nKey, {
              ns: TranslationNamespace.PROFILE,
            });
            return (
              <Tab
                data-element_id={groupTabObject[tabId]?.mixpanelButtonId}
                key={tabId}
                value={tabId}
                style={
                  isOnMobile
                    ? propsProfileTabsGroup.length - 1 === index
                      ? lastTabStyle
                      : tabStyle
                    : undefined
                }
                labelStyle={tabLabelStyle}
                label={<TabText isActive={isActive}>{title}</TabText>}
              />
            );
          })}
        </Tabs>
      </GroupTabs>
    );
  }, [
    handlePublishStatusChange,
    isMeProfile,
    isOnMobile,
    propsProfileTabsGroup,
    publishStatus,
    t,
  ]);
  const ListTabsComponent = useMemo(() => {
    // Hide list tab if only have one or no item.
    if (listTabs.length <= 1) return null;
    return (
      <ListTabs>
        <Tabs
          value={listType}
          onChange={handleTabChange}
          isScrollable={isOnMobile}
        >
          {listTabs.map((tabId, index) => {
            const isActive = tabId === listType;
            const i18nKey = tabObject[tabId]?.titleI18nKey;
            const title = t(i18nKey, {
              ns: TranslationNamespace.PROFILE,
            });
            const { pathname, search } = getPath({
              listType: tabId,
              publishStatus,
            });
            const tabHref = `${pathname}${search}`;
            return (
              <Tab
                data-element_id={tabObject[tabId]?.mixpanelButtonId}
                key={tabId}
                value={tabId}
                style={
                  isOnMobile
                    ? listTabs.length - 1 === index
                      ? lastTabStyle
                      : tabStyle
                    : undefined
                }
                labelStyle={tabLabelStyle}
                label={<TabText isActive={isActive}>{title}</TabText>}
                href={tabHref}
              />
            );
          })}
        </Tabs>
      </ListTabs>
    );
  }, [
    handleTabChange,
    isOnMobile,
    listType,
    t,
    listTabs,
    getPath,
    publishStatus,
  ]);

  return (
    <StyledProfileTab>
      {GroupsTabsComponent}
      {ListTabsComponent}
    </StyledProfileTab>
  );
};

ProfileTab.propTypes = {
  location: PropTypes.object,
  userId: PropTypes.string,
  listType: PropTypes.string,
  publishStatus: PropTypes.string,
  deviceWidth: PropTypes.number,
  isMeProfile: PropTypes.bool,
  profileTabs: PropTypes.array,
  profileTabsDraft: PropTypes.array,
  profileTabsGroup: PropTypes.array,
  replace: PropTypes.func,
};

const StyledProfileTab = styled.div`
  position: sticky;
  top: ${headerHeight}px;
  left: 0px;
  right: 0px;
  z-index: 2;
  background-color: ${color.black};
`;

const GroupTabs = styled.div`
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
`;

const ListTabs = styled.div`
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
`;

const TabText = styled.h2`
  font-size: 16px;
  margin: 0;
  font-weight: normal;
  ${media.mobile`
    font-size: 14px;
  `};
`;

export default ProfileTab;
